import { GAIL_BLUE_CLASS_NAME } from "@/components/common/GailBlue";

type MdastNode = {
  type: string;
  value?: string;
  children?: MdastNode[];
  data?: {
    hName?: string;
    hProperties?: { className?: string[] };
  };
};

function textNode(value: string): MdastNode {
  return { type: "text", value };
}

function gailBlueNode(children: MdastNode[]): MdastNode {
  return {
    type: "gailBlue",
    data: {
      hName: "span",
      hProperties: { className: GAIL_BLUE_CLASS_NAME.split(" ") },
    },
    children,
  };
}

function findClose(
  children: MdastNode[],
  openIndex: number,
  openOffset: number,
): { index: number; offset: number } | null {
  const openNode = children[openIndex];
  if (openNode?.type === "text" && openNode.value) {
    const sameNode = openNode.value.indexOf("))", openOffset + 2);
    if (sameNode !== -1) {
      return { index: openIndex, offset: sameNode };
    }
  }

  for (let index = openIndex + 1; index < children.length; index += 1) {
    const candidate = children[index];
    if (candidate?.type === "text" && candidate.value?.includes("))")) {
      return { index, offset: candidate.value.indexOf("))") };
    }
  }

  return null;
}

function mapNode(node: MdastNode): MdastNode {
  if (!node.children) return node;
  return { ...node, children: transformChildren(node.children) };
}

function isMarkerText(
  node: MdastNode | undefined,
): node is MdastNode & { value: string } {
  return node?.type === "text" && Boolean(node.value?.includes("(("));
}

function innerNodes(
  children: MdastNode[],
  index: number,
  openAt: number,
  close: { index: number; offset: number },
): MdastNode[] {
  const value = children[index]?.value ?? "";
  if (close.index === index) {
    const innerText = value.slice(openAt + 2, close.offset);
    return innerText ? [textNode(innerText)] : [];
  }

  const inner: MdastNode[] = [];
  const openRemainder = value.slice(openAt + 2);
  if (openRemainder) inner.push(textNode(openRemainder));
  inner.push(...children.slice(index + 1, close.index));
  const closeBefore = (children[close.index]?.value ?? "").slice(0, close.offset);
  if (closeBefore) inner.push(textNode(closeBefore));
  return inner;
}

function nodesAfter(
  children: MdastNode[],
  index: number,
  value: string,
  offset: number,
): MdastNode[] {
  const rest = value.slice(offset);
  return [...(rest ? [textNode(rest)] : []), ...children.slice(index + 1)];
}

function transformMarked(
  children: MdastNode[],
  index: number,
  value: string,
  openAt: number,
): MdastNode[] {
  const close = findClose(children, index, openAt);
  if (!close) {
    return [
      textNode(value.slice(0, openAt + 2)),
      ...transformChildren(nodesAfter(children, index, value, openAt + 2)),
    ];
  }

  const before = value.slice(0, openAt);
  const inner = transformChildren(innerNodes(children, index, openAt, close));
  const after = nodesAfter(
    children,
    close.index,
    children[close.index]?.value ?? "",
    close.offset + 2,
  );

  return [
    ...(before ? [textNode(before)] : []),
    ...(inner.length > 0 ? [gailBlueNode(inner)] : []),
    ...transformChildren(after),
  ];
}

function transformChildren(children: MdastNode[]): MdastNode[] {
  const result: MdastNode[] = [];

  for (let index = 0; index < children.length; index += 1) {
    const node = children[index];
    if (!isMarkerText(node)) {
      if (node) result.push(mapNode(node));
      continue;
    }

    result.push(
      ...transformMarked(children, index, node.value, node.value.indexOf("((")),
    );
    break;
  }

  return result;
}

/**
 * Turns `((text))` markers into spans after markdown is parsed, so nested
 * formatting such as `((**bold**))` stays markdown and the browser never
 * evaluates generated JavaScript.
 */
export function remarkGailBlue() {
  return (tree: { children?: MdastNode[] }) => {
    if (tree.children) {
      tree.children = transformChildren(tree.children);
    }
  };
}
